import { Container } from "inversify";
import { ConfigurationBuilder, ConfigurationBuilderImpl } from "../configurationBuilder/ConfigurationBuilder";
import { ErrorReporter, ErrorReporterImpl } from "../errorReporter/ErrorReporter";
import { IframeNotifier, IframeNotifierImpl } from "../iframeNotifier/IframeNotifier";
import { AnimationLayer } from "../layer/AnimationLayer";
import { ControlsLayer } from "../layer/ControlsLayer";
import { CTALayer } from "../layer/CTALayer";
import { PlayerContainer } from "../layer/PlayerContainer";
import { PosterLayer } from "../layer/PosterLayer";
import { VideoLayer } from "../layer/VideoLayer";
import { PlayerLoader, PlayerLoaderImpl } from "../loader/playerLoader/PlayerLoader";
import { ResourceLoader, ResourceLoaderImpl } from "../loader/resourceLoader/ResourceLoader";
import { ScenarioLoader, ScenarioLoaderImpl } from "../loader/scenarioLoader/ScenarioLoader";
import { NotSupportedBrowserPlayerManagerImpl } from "../playerManager/NotSupportedBrowserPlayerManagerImpl";
import { AnimationPersonalizer, AnimationPersonalizerImpl } from "../personalizer/AnimationPersonalizer";
import { Personalizer, PersonalizerImpl } from "../personalizer/Personalizer";
import { AnalyticsCollector, AnalyticsCollectorImpl } from "../playerAnalytics/AnalyticsCollector";
import { GoogleAnalyticsSender, GoogleAnalyticsSenderImpl } from "../playerAnalytics/GoogleAnalyticsSender";
import { VintomAnalyticsSender, VintomAnalyticsSenderImpl } from "../playerAnalytics/VintomAnalyticsSender";
import { PlayerManager } from "../playerManager/PlayerManager";
import { PlayerManagerImpl } from "../playerManager/PlayerManagerImpl";
import { PreferencesStorage, PreferencesStorageImpl } from "../preferencesStorage/PreferencesStorage";
import { AnimationDrivenScenarioExecutorImpl } from "../scenarioRunner/AnimationDrivenScenarioExecutor";
import { CTAOnlyScenarioExecutorImpl } from "../scenarioRunner/CTAOnlyScenarioExecutor";
import { ControlsLayerManager, ControlsLayerManagerImpl } from "../scenarioRunner/layerManager/ControlsLayerManager";
import { CTALayerManager, CTALayerManagerImpl } from "../scenarioRunner/layerManager/CTALayerManager";
import {
  DependentAnimationLayerManager,
  DependentAnimationLayerManagerImpl,
} from "../scenarioRunner/layerManager/DependentAnimationLayerManager";
import {
  IndependentAnimationLayerManager,
  IndependentAnimationLayerManagerImpl,
} from "../scenarioRunner/layerManager/IndependentAnimationLayerManager";
import { PlayerContainerManager, PlayerContainerManagerImpl } from "../scenarioRunner/layerManager/PlayerContainerManager";
import { PosterLayerManager, PosterLayerManagerImpl } from "../scenarioRunner/layerManager/PosterLayerManager";
import { VideoLayerManager, VideoLayerManagerImpl } from "../scenarioRunner/layerManager/VideoLayerManager";
import { ScenarioExecutor } from "../scenarioRunner/ScenarioExecutor";
import { ScenarioRunner, ScenarioRunnerImpl } from "../scenarioRunner/ScenarioRunner";
import { VideoDrivenScenarioExecutorImpl } from "../scenarioRunner/VideoDrivenScenarioExecutor";
import { ThemeUpdater, ThemeUpdaterImpl } from "../themeUpdater/ThemeUpdater";
import { TYPES } from "./types";
import { MusicPlayer, MusicPlayerImpl } from "../../manager/scenarioRunner/musicManager/MusicPlayer";
import { VolumeTransitionManager, VolumeTransitionManagerImpl } from "../../manager/scenarioRunner/musicManager/VolumeTransitions";
import { PlayerMusicManager, PlayerMusicManagerImpl } from "../../manager/scenarioRunner/musicManager/PlayerSyncedMusicManager";
import { DebugLayerHandle } from "../../components/layer/DebugLayerComponent";

export const createApplicationContext = (
  playerContainer: PlayerContainer,
  animationLayer: AnimationLayer,
  controlsLayer: ControlsLayer,
  ctaLayer: CTALayer,
  posterLayer: PosterLayer,
  videoLayer: VideoLayer,
  playbackInformation: DebugLayerHandle,
): Container => {
  const container = new Container();

  container.bind<PlayerContainer>(TYPES.layer.PlayerContainer).toConstantValue(playerContainer);
  container.bind<AnimationLayer>(TYPES.layer.AnimationLayer).toConstantValue(animationLayer);
  container.bind<ControlsLayer>(TYPES.layer.ControlsLayer).toConstantValue(controlsLayer);
  container.bind<CTALayer>(TYPES.layer.CTALayer).toConstantValue(ctaLayer);
  container.bind<PosterLayer>(TYPES.layer.PosterLayer).toConstantValue(posterLayer);
  container.bind<VideoLayer>(TYPES.layer.VideoLayer).toConstantValue(videoLayer);
  container.bind<DebugLayerHandle>(TYPES.scenarioRunner.playbackInformation).toConstantValue(playbackInformation);

  container.bind<AnalyticsCollector>(TYPES.analyticsCollector.AnalyticsCollector).to(AnalyticsCollectorImpl).inSingletonScope();
  container.bind<GoogleAnalyticsSender>(TYPES.analyticsCollector.GoogleAnalyticsSender).to(GoogleAnalyticsSenderImpl).inSingletonScope();
  container.bind<VintomAnalyticsSender>(TYPES.analyticsCollector.VintomAnalyticsSender).to(VintomAnalyticsSenderImpl).inSingletonScope();

  container.bind<PreferencesStorage>(TYPES.preferencesStorage.PreferencesStorage).to(PreferencesStorageImpl).inSingletonScope();
  container.bind<ErrorReporter>(TYPES.errorReporter.ErrorReporter).to(ErrorReporterImpl).inSingletonScope();
  container.bind<IframeNotifier>(TYPES.iframeNotifier.IframeNotifier).to(IframeNotifierImpl).inSingletonScope();
  container.bind<AnimationPersonalizer>(TYPES.animationPersonalizer.AnimationPersonalizer).to(AnimationPersonalizerImpl).inSingletonScope();
  container.bind<Personalizer>(TYPES.personalizer.Personalizer).to(PersonalizerImpl).inSingletonScope();
  container.bind<ConfigurationBuilder>(TYPES.configurationBuilder.ConfigurationBuilder).to(ConfigurationBuilderImpl).inSingletonScope();
  container.bind<ThemeUpdater>(TYPES.themeUpdater.ThemeUpdater).to(ThemeUpdaterImpl).inSingletonScope();

  container.bind<PlayerLoader>(TYPES.loader.playerLoader.PlayerLoader).to(PlayerLoaderImpl).inSingletonScope();
  container.bind<ResourceLoader>(TYPES.loader.resourceLoader.ResourceLoader).to(ResourceLoaderImpl).inSingletonScope();
  container.bind<ScenarioLoader>(TYPES.loader.scenarioLoader.ScenarioLoader).to(ScenarioLoaderImpl).inSingletonScope();

  container
    .bind<PlayerContainerManager>(TYPES.scenarioRunner.layerManager.PlayerContainerManager)
    .to(PlayerContainerManagerImpl)
    .inSingletonScope();
  container
    .bind<ControlsLayerManager>(TYPES.scenarioRunner.layerManager.ControlsLayerManager)
    .to(ControlsLayerManagerImpl)
    .inSingletonScope();
  container.bind<CTALayerManager>(TYPES.scenarioRunner.layerManager.CTALayerManager).to(CTALayerManagerImpl).inSingletonScope();
  container
    .bind<DependentAnimationLayerManager>(TYPES.scenarioRunner.layerManager.DependentAnimationLayerManager)
    .to(DependentAnimationLayerManagerImpl)
    .inSingletonScope();
  container
    .bind<IndependentAnimationLayerManager>(TYPES.scenarioRunner.layerManager.IndependentAnimationLayerManager)
    .to(IndependentAnimationLayerManagerImpl)
    .inSingletonScope();
  container.bind<PosterLayerManager>(TYPES.scenarioRunner.layerManager.PosterLayerManager).to(PosterLayerManagerImpl).inSingletonScope();
  container.bind<VideoLayerManager>(TYPES.scenarioRunner.layerManager.VideoLayerManager).to(VideoLayerManagerImpl).inSingletonScope();

  container.bind<PlayerMusicManager>(TYPES.music.MusicManager).to(PlayerMusicManagerImpl).inSingletonScope();
  container.bind<MusicPlayer>(TYPES.music.MusicPlayer).to(MusicPlayerImpl).inSingletonScope();
  container.bind<VolumeTransitionManager>(TYPES.music.VolumeTransitionManager).to(VolumeTransitionManagerImpl).inSingletonScope();

  container
    .bind<ScenarioExecutor>(TYPES.scenarioRunner.AnimationDrivenScenarioExecutor)
    .to(AnimationDrivenScenarioExecutorImpl)
    .inSingletonScope();
  container.bind<ScenarioExecutor>(TYPES.scenarioRunner.VideoDrivenScenarioExecutor).to(VideoDrivenScenarioExecutorImpl).inSingletonScope();
  container.bind<ScenarioExecutor>(TYPES.scenarioRunner.CTAOnlyScenarioExecutor).to(CTAOnlyScenarioExecutorImpl).inSingletonScope();
  container.bind<ScenarioRunner>(TYPES.scenarioRunner.ScenarioRunner).to(ScenarioRunnerImpl).inSingletonScope();

  container.bind<PlayerManager>(TYPES.PlayerManager).to(PlayerManagerImpl).inSingletonScope();

  return container;
};

export const createNotSupportedBrowserApplicationContext = (): Container => {
  const container = new Container();

  container.bind<AnalyticsCollector>(TYPES.analyticsCollector.AnalyticsCollector).to(AnalyticsCollectorImpl).inSingletonScope();
  container.bind<GoogleAnalyticsSender>(TYPES.analyticsCollector.GoogleAnalyticsSender).to(GoogleAnalyticsSenderImpl).inSingletonScope();
  container.bind<VintomAnalyticsSender>(TYPES.analyticsCollector.VintomAnalyticsSender).to(VintomAnalyticsSenderImpl).inSingletonScope();

  container.bind<ConfigurationBuilder>(TYPES.configurationBuilder.ConfigurationBuilder).to(ConfigurationBuilderImpl).inSingletonScope();
  container.bind<ResourceLoader>(TYPES.loader.resourceLoader.ResourceLoader).to(ResourceLoaderImpl).inSingletonScope();

  container.bind<PlayerManager>(TYPES.PlayerManager).to(NotSupportedBrowserPlayerManagerImpl).inSingletonScope();

  return container;
};
